import { maybeWebContext } from "../search";
// chat/brain.ts — the chat brain, SERVER-FIRST (the R8 graduation, mirrors study_companion).
// askBrain now sends the message + this app's declared action catalog to the generic fleet
// chat brain (/api/apps/oriora_muster/chat) — ONE billed call returns ONE registered action +
// a reply; the app resolves/executes it locally so the collection stays on-device. The regex
// intent-router below is the FREE FALLBACK (offline / signed-out / out of funds) — chat never
// goes dead. Original prototype notes:
//   • "add unit <name> to <army>[, N points][, M models]" → local store.createUnit   → add_unit
//   • "mark <unit> as <status>" / "set <unit> to <status>" → local store.updateUnit  → set_status (the one edit)
//   • "delete <unit>" / "remove <unit>"                    → resolve id locally      → delete_unit (UI confirms)
//   • "show/list/find/search <term|status|army>"           → local list + filter     → search_units (free)
//   • "recap" / "summarize my collection" / "progress report" → EXISTING recap call  → recap (billed, fenced)
//   • anything else                                        → none + a short HELP nudge
//
// FENCE (HARD, MANDATORY pre-LLM on BOTH paths). Muster describes the user's OWN logged
// collection; it NEVER gives advice, recommends a purchase, or judges a scheme/army/model
// choice. isAdviceRequest() runs on the raw message BEFORE serverChat is ever called — an
// advice-shaped ask is refused for FREE, so it never reaches the (billed) routing call
// either, not just the (billed) recap call one step later (whose signals payload is ALSO
// gated by assertObservational — defence in depth). Pre-graduation Muster had NO free-text
// chat surface at all (only a computed signals object ever reached the model); now that
// chat sends every message to the model, this is the fence's FIRST live load-bearing duty.
//
// The "recap" action stays a TWO-STEP local resolve (like Lockbox's "ask" / Mat Time's
// "recap"): the ChatAction union's `recap` variant already carries the FULLY-RESOLVED
// narration text (not just a request marker), so toAction() below computes the on-device
// signals AND calls the existing billed /recap endpoint itself — exactly what the original
// keyword router did. Deliberate deviation from the lighter "explain"/"make" shape used
// elsewhere (where the second billed call runs later, inside chat/actions.ts) — forced by
// actions.ts's ChatAction type, which this migration must not change.

import { store, todayLocal, STATUSES, PAINT_TYPES, type Faction, type Status, type Unit,
  type NewFaction, type FactionPatch, type UnitPatch,
  type Paint, type NewPaint, type PaintPatch, type Recipe, type NewRecipe, type RecipePatch } from "../store";
import { recapCollection, isAdviceRequest, brainCall, BrainError } from "../brain";
import { findNamedMatches, notFoundMessage } from "./fuzzy";
import type { ChatAction, ResultRow } from "./actions";
import { libraryFind, librarySummary } from "../library";

export interface ChatTurn {
  role: "user" | "assistant";
  content: string;
  rows?: ResultRow[];
}
export interface BrainResponse {
  reply: string;         // FENCE: kept "" — never surface the model's freeform reply (advice-fenced app).
  actions: ChatAction[]; // the shared brain returns an ARRAY — two asks in one message = two actions (G17).
}
/** One resolved action (what toAction/routeMessage build); serverChat/askBrain assemble the array. */
interface One { reply: string; action: ChatAction }

// Sent to the shared chat brain as `scope` (defense-in-depth on top of isAdviceRequest + reply-discard).
const SCOPE =
  "Hard limits: never recommend a purchase, judge whether an army/unit/scheme/faction is good/bad/strong/" +
  "competitive, or advise what to buy, paint, or play next — decline plainly. This app is CAN-only: log/" +
  "edit/delete armies, units, paints and recipes, set a unit's build/paint status, search them, and give " +
  "an OBSERVATIONAL recap of the user's OWN logged collection (points painted vs unpainted, WIP ages); " +
  "back up to their own cloud. A paint's `wanted` field marks it as owned (0) or on the user's own " +
  "shopping list of paints they still need to buy (1) — this is the user tracking their OWN to-buy list, " +
  "not the app recommending a purchase; set it via add_paint/edit_paint when they ask.";

const HELP =
  "Tell me what to do with your collection — add a unit, mark its progress, find units, or get a recap. Try:\n" +
  '• "add unit Terminator Squad to Space Marines, 200 points, 5 models"\n' +
  '• "mark Terminator Squad as painted"\n' +
  '• "show wip units" or "find Ork Boyz"\n' +
  '• "recap my collection"\n' +
  '• "delete Ork Boyz"';

const none = (message: string): One => ({ reply: "", action: { kind: "none", message } });

// Unit name→id resolution (fleet FUZZY standard, fuzzy.ts): exact substring first, then a forgiving
// near-match so a mistyped/paraphrased unit name still resolves ("termintor" → "Terminator Squad").
// Client-side — units live on-device. Used by set-status / delete / and the chat photo attach.
export async function findUnitByName(term: string): Promise<Unit[]> {
  const q = term.trim().toLowerCase().replace(/[.?!]+$/, "");
  if (!q) return [];
  const all = await store.listUnits();
  const subs = all.filter((u) => u.name.toLowerCase().includes(q));
  if (subs.length) return subs;
  return findNamedMatches(all, term); // Unit has `name`
}
/** "Couldn't find X" enriched with the closest unit names ("did you mean …?"). */
async function unitNotFound(term: string): Promise<string> {
  const all = await store.listUnits();
  return notFoundMessage(all, term);
}

const ADVICE_REFUSAL = "Muster describes your collection — it can't give advice or recommendations.";

// Paint / recipe name resolution (same fuzzy standard). Client-side; data lives on-device.
async function findPaintByName(term: string): Promise<Paint[]> {
  const q = term.trim().toLowerCase().replace(/[.?!]+$/, "");
  if (!q) return [];
  const all = await store.listPaints();
  const subs = all.filter((pt) => pt.name.toLowerCase().includes(q) || `${pt.brand} ${pt.name}`.trim().toLowerCase().includes(q));
  if (subs.length) return subs;
  return findNamedMatches(all, term); // Paint has `name`
}
async function findRecipeByName(term: string): Promise<Recipe[]> {
  const q = term.trim().toLowerCase().replace(/[.?!]+$/, "");
  if (!q) return [];
  const all = await store.listRecipes();
  const subs = all.filter((rc) => rc.name.toLowerCase().includes(q));
  if (subs.length) return subs;
  return findNamedMatches(all, term); // Recipe has `name`
}
const asPaintType = (v: unknown): string | undefined => {
  const s = asStr(v)?.toLowerCase();
  return s && PAINT_TYPES.includes(s) ? s : undefined;
};

const STATUS_ALT = STATUSES.join("|");
// Explicit recap intents — kept tight so it only fires the ONE billed call on a real ask.
const RECAP_RE = /\b(recap|summar(?:y|ize|ise)|progress\s+report|how'?s?\s+my\s+(?:collection|army|progress))\b/i;

export async function askBrain(history: ChatTurn[], onChunk?: (t: string) => void): Promise<BrainResponse> {
  const last = [...history].reverse().find((t) => t.role === "user");
  const text = (last?.content ?? "").trim();
  if (!text) { const r = none(HELP); return { reply: r.reply, actions: [r.action] }; }
  // FENCE — pre-LLM, free, on BOTH paths. An advice/recommendation ask is refused
  // deterministically before it can reach EITHER billed call (routing OR recap).
  if (isAdviceRequest(text)) { const r = none(ADVICE_REFUSAL); return { reply: r.reply, actions: [r.action] }; }
  try {
    return await serverChat(history, text, onChunk);
  } catch (e) {
    // A money/auth/key refusal must be SAID, not masked: the local fallback answering a
    // funds-blocked question as if it were a search reads as a broken app (found live 2026-08-27).
    if (e instanceof BrainError && (e.code === "insufficient_funds" || e.code === "unauthenticated" || e.code === "managed_ai_blocked")) {
      return { reply: e.message, actions: [{ kind: "none" }] };
    }
    // Offline / transient server trouble → the free local intent-router below
    // still answers; chat never goes dead. (Local path handles one intent → one action.)
    const r = await routeMessage(text);
    return { reply: r.reply, actions: [r.action] };
  }
}

// ---- SERVER CHAT-BRAIN (the graduated path) --------------------------------
// The app declares WHAT it can do; the fleet chat brain decides WHICH action the message
// means; this file resolves references (army/unit name text → id) and runs the ONE billed
// recap call locally, because the collection lives here.

// Standing instructions: the customer shapes the app through chat; stored on-device, sent every call.
const INSTRUCTIONS_KEY = "oriora.muster.instructions";
export const getInstructions = (): string => { try { return localStorage.getItem(INSTRUCTIONS_KEY) ?? ""; } catch { return ""; } };
export const addInstruction = (line: string): void => { try { const cur = getInstructions(); localStorage.setItem(INSTRUCTIONS_KEY, cur ? cur + "\n- " + line : "- " + line); } catch { /* ignore */ } };

export const CHAT_ACTIONS = [
  { name: "add_unit", description: "Log a new unit to the collection.", params: {
      name: "REQUIRED. The unit's name.",
      faction: "Optional army/faction name to file it under, as the user wrote it. If the collection has only one army, it's used automatically.",
      points: "Optional points value.",
      model_count: "Optional number of models in the unit.",
  } },
  { name: "set_status", description: `Update a unit's build/paint status — the one supported edit. Status is one of: ${STATUSES.join(", ")}.`, params: {
      target: "REQUIRED. Which unit — name text as the user wrote it.",
      status: `REQUIRED. One of: ${STATUSES.join(", ")}.`,
  } },
  { name: "edit_unit", description: "Change a unit's details — rename, points, model count, notes, or re-file it under a different army. For only its build/paint status use set_status. Give at least one field.", params: {
      target: "REQUIRED. Which unit — name text as the user wrote it.",
      name: "new name, only if renaming.",
      points: "new points value, only if changing.",
      model_count: "new number of models, only if changing.",
      faction: "new army/faction name to move it to, only if re-filing.",
      notes: "new notes, only if changing.",
  } },
  { name: "delete_unit", description: "Delete one unit identified by its name (the app asks the user to confirm first).", params: { target: "Which unit — name text as the user wrote it." } },
  { name: "add_faction", description: "Add a new army/faction to the collection.", params: { name: "REQUIRED — the army's name", game_system: "optional game system, e.g. Warhammer 40k", notes: "optional note" } },
  { name: "edit_faction", description: "Change an army's name, game system, or notes. Give at least one field.", params: { target: "REQUIRED — which army, name as the user wrote it", name: "new name, only if renaming", game_system: "new game system, only if changing", notes: "new notes, only if changing" } },
  { name: "delete_faction", description: "Delete one army and its units (the app asks the user to confirm first).", params: { target: "REQUIRED — which army, name as the user wrote it" } },
  { name: "add_paint", description: "Add a paint to the inventory.", params: { name: "REQUIRED — the paint's name", brand: "optional brand", paint_type: `optional type: ${PAINT_TYPES.join("/")}`, color_family: "optional colour family, e.g. red, blue", qty: "optional number of bottles owned" } },
  { name: "edit_paint", description: "Change a paint's fields — name, brand, type, colour family, or bottle count (qty). Identify it by name. Give at least one field.", params: { target: "REQUIRED — which paint, name as the user wrote it", name: "new name, only if renaming", brand: "new brand, only if changing", paint_type: `new type, only if changing: ${PAINT_TYPES.join("/")}`, color_family: "new colour family, only if changing", qty: "new bottle count, only if changing" } },
  { name: "delete_paint", description: "Remove one paint from the inventory by its name (the app asks the user to confirm first).", params: { target: "REQUIRED — which paint, name as the user wrote it" } },
  { name: "add_recipe", description: "Add a paint recipe (a named sequence of steps).", params: { name: "REQUIRED — the recipe's name", steps: "optional steps, separated by | (pipe)", notes: "optional note" } },
  { name: "edit_recipe", description: "Change a recipe's name, steps, or notes. Identify it by name. Give at least one field.", params: { target: "REQUIRED — which recipe, name as the user wrote it", name: "new name, only if renaming", steps: "new steps, separated by |, only if changing", notes: "new notes, only if changing" } },
  { name: "delete_recipe", description: "Delete one recipe by its name (the app asks the user to confirm first).", params: { target: "REQUIRED — which recipe, name as the user wrote it" } },
  { name: "search_units", description: "Search / list the user's logged units.", params: {
      query: "Optional free text to match against unit names.",
      status: `Optional status filter — one of: ${STATUSES.join(", ")}.`,
      faction: "Optional army/faction name to filter by.",
  } },
  { name: "recap", description: "Narrate the user's collection progress from their own logged figures (points painted/unpainted, WIP ages, recipe use) — observational only, NEVER advice or recommendations." },
  { name: "backup_save", description: "Back up the WHOLE collection to the customer's own GitHub or Google Drive (connected once in their Oriora account). Works for ANY size — if the backup is too large for the standard path, it saves straight from this device to their GitHub when that's logged in (backup menu), or the reply tells them the one-time login to enable it.", params: {} },
  { name: "backup_status", description: "Tell the customer WHERE their data is backed up: which cloud is connected in their Oriora account, which one new backups write to, and when the last backup was. Use for 'where is my data / is my stuff backed up / which cloud do you use'.", params: {} },
  { name: "backup_restore", description: "Restore from the customer's cloud backup — REPLACES current data (the app asks the user to confirm first). If they NAME a source ('restore from Drive'), pass provider.", params: { provider: "optional github|gdrive when the customer names which cloud to restore from" } },
  { name: "save_to_cloud", description: "Save a note, or THIS conversation, to the customer's OWN GitHub or Google Drive. Use when they ask to store/save/keep the conversation, their prompts/messages, or a note (NOT the whole collection — that's backup_save).", params: { content_kind: "\"conversation\" to save this whole chat, or \"note\" for a piece of text", note: "the text to save when content_kind is \"note\"", destination: "optional \"github\" or \"gdrive\" if the user names one; omit otherwise" } },
  { name: "save_local_copy", description: "Save a one-off copy of the user's data to a folder/location they pick on THIS device (the OS picker). Works on iPhone and Mac. Use for 'save my data to a file' / 'save a copy locally'.", params: {} },
  { name: "import_file", description: "Import / restore the user's data from a FILE they pick on this device — Files, iCloud, a folder, or a backup from another device. REPLACES current data (the app asks them to confirm first). Use for 'import my data', 'restore from a file', 'load my backup file'.", params: {} },
  { name: "remember", description: "Save a standing instruction for how THIS customer wants the app to behave from now on.", params: { instruction: "the preference, one sentence" } },
  // ── ARTIFACT TOOLS — the customer's own library (device-only). Whatever the AI
  // makes for them gets KEPT here instead of vanishing into the chat. Fleet-wide
  // capability, identical in every Oriora app.
  { name: "library_save", description: "Save a plan, summary, guide or note into the customer's ON-DEVICE library. Use whenever you produce something worth keeping, or they ask you to save/remember/write something down. When they ask you to RESEARCH something and make them a plan: write the FULL text in \`content\`, give it a clear title, and put a short summary in your reply.", params: { title: "short title", item_kind: "lesson | plan | note", content: "the FULL text to save — write it out completely", folder: "optional folder name" } },
  { name: "library_list", description: "List what the customer has saved in their library ('what have I saved', 'show my plans', 'what's in my library / in folder X').", params: { folder: "optional folder to filter by" } },
  { name: "library_remove", description: "Remove ONE library item by its title (the app asks the customer to confirm first). LIBRARY items only — the app's own entries have their own delete action.", params: { title: "which item (its title, from LIBRARY in the context)" } },
  { name: "library_move", description: "Move a library item into a folder ('move the summer plan into Plans').", params: { title: "which item", folder: "target folder name" } },
  { name: "library_merge", description: "Merge two or more library items into one (the app asks the customer to confirm first).", params: { titles: "the titles to merge, separated by |", new_title: "title for the merged item" } },
];

interface ServerAct { name?: string; action?: string; params?: Record<string, unknown> }
interface ServerChat { actions?: ServerAct[]; action?: string; params?: Record<string, unknown>; reply?: string }

async function buildContext(): Promise<string> {
  const parts: string[] = [];
  try {
    const factions = await store.listFactions();
    if (factions.length) parts.push(`Armies: ${factions.map((f) => f.name).join(", ")}.`);
    const units = await store.listUnits();
    if (units.length) parts.push(`${units.length} units logged.`);
  } catch { /* context is optional */ }
  return parts.join(" ");
}

async function serverChat(history: ChatTurn[], text: string, onChunk?: (t: string) => void): Promise<BrainResponse> {
  const _notes = getInstructions();
  const context = (await buildContext()) + (_notes ? "\nCustomer's standing instructions (apply when filling params): " + _notes : "");
  const recent = history.slice(-21, -1).map((t) => ({ role: t.role, content: t.content }));
  const r = await brainCall<ServerChat>("chat", { message: text + (await maybeWebContext(text)), actions: CHAT_ACTIONS, context, history: recent, scope: SCOPE }, onChunk);
  // The shared brain returns an ARRAY (G17): "add X and mark it painted" = two actions, run in order.
  // FENCE: never surface the model's freeform reply — reply stays "".
  const raw: ServerAct[] = Array.isArray(r.actions) && r.actions.length
    ? r.actions
    : (r.action ? [{ action: r.action, params: r.params }] : []);   // back-compat / older server
  if (!raw.length) return { reply: "", actions: [{ kind: "none", message: HELP }] };
  const actions: ChatAction[] = [];
  for (const a of raw) {
    const one = await toAction({ action: a.name ?? a.action ?? "", params: a.params, reply: "" }, text);
    actions.push(one.action);
  }
  return { reply: "", actions };
}

const asStr = (v: unknown): string | undefined => (typeof v === "string" && v.trim() ? v.trim() : undefined);
const asInt = (v: unknown): number | undefined => {
  const n = typeof v === "number" ? v : parseInt(String(v ?? ""), 10);
  return Number.isFinite(n) && n >= 0 ? Math.round(n) : undefined;
};
const asStatus = (v: unknown): Status | undefined => {
  const s = asStr(v)?.toLowerCase();
  if (!s) return undefined;
  if ((STATUSES as readonly string[]).includes(s)) return s as Status;
  if (/work[\s-]?in[\s-]?progress|\bwip\b/.test(s)) return "wip";
  return undefined;
};

export async function resolveFaction(factionQuery?: string): Promise<{ ok: true; faction: Faction } | { ok: false; message: string }> {
  const factions = await store.listFactions();
  if (factions.length === 0) return { ok: false, message: "Add an army first (Collection tab → Add army) — then I can log units to it." };
  if (factionQuery) {
    const fq = factionQuery.toLowerCase();
    const matches = factions.filter((f) => f.name.toLowerCase().includes(fq));
    if (matches.length === 0) {
      const list = factions.map((f) => `• ${f.name}`).join("\n");
      return { ok: false, message: `No army matching "${factionQuery}". You have:\n${list}` };
    }
    if (matches.length > 1) {
      const list = matches.map((f) => `• ${f.name}`).join("\n");
      return { ok: false, message: `${matches.length} armies match "${factionQuery}" — be more specific:\n${list}` };
    }
    return { ok: true, faction: matches[0] };
  }
  if (factions.length === 1) return { ok: true, faction: factions[0] };
  const list = factions.map((f) => `• ${f.name}`).join("\n");
  return { ok: false, message: `Which army? You have:\n${list}` };
}

export async function toAction(r: ServerChat, rawText: string): Promise<One> {
  const p = r.params ?? {};
  const say = asStr(r.reply) ?? "";

  // ── ARTIFACT TOOLS (the library) — handled EARLY, above any app-specific
  // precondition (no vehicle / no plant / nothing selected). The library belongs
  // to the customer, not to a currently-active record. (Garage shipped this bug:
  // its no-vehicle guard silently swallowed every library action, 2026-08-13.)
  if (r.action === "library_save") {
    const _content = asStr(p.content);
    if (!_content) return none("Tell me what to save — or ask me to write it first and I'll keep it.");
    const _rawKind = (asStr(p.item_kind) ?? "note").toLowerCase();
    const _itemKind = _rawKind === "lesson" || _rawKind === "plan" ? _rawKind : "note";
    return { reply: say, action: { kind: "library_save", title: asStr(p.title) ?? "Untitled", itemKind: _itemKind, content: _content, folder: asStr(p.folder) } };
  }
  if (r.action === "library_list") return { reply: say, action: { kind: "library_list", folder: asStr(p.folder) } };
  if (r.action === "library_remove") {
    const _r = resolveLibraryItem(asStr(p.title));
    if (!_r.item) return none(_r.problem!);
    return { reply: say, action: { kind: "library_remove", id: _r.item.id, label: `"${_r.item.title}"` } };
  }
  if (r.action === "library_move") {
    const _folder = asStr(p.folder);
    if (!_folder) return none("Which folder should it go to?");
    const _r2 = resolveLibraryItem(asStr(p.title));
    if (!_r2.item) return none(_r2.problem!);
    return { reply: say, action: { kind: "library_move", id: _r2.item.id, folder: _folder, label: `"${_r2.item.title}"` } };
  }
  if (r.action === "library_merge") {
    const _titles = (asStr(p.titles) ?? "").split("|").map((s) => s.trim()).filter(Boolean);
    if (_titles.length < 2) return none("Tell me which two (or more) items to merge, by title.");
    const _ids: string[] = [];
    for (const _t of _titles) {
      const _r3 = resolveLibraryItem(_t);
      if (!_r3.item) return none(_r3.problem!);
      _ids.push(_r3.item.id);
    }
    return { reply: say, action: { kind: "library_merge", ids: _ids, title: asStr(p.new_title) ?? _titles.join(" + "), label: _titles.map((t) => `"${t}"`).join(" + ") } };
  }
  switch (r.action) {
    case "add_unit": {
      const name = asStr(p.name);
      if (!name) return none(say || 'Give me a unit name, e.g. "add unit Terminator Squad to Space Marines, 200 points, 5 models".');
      const fr = await resolveFaction(asStr(p.faction));
      // Not resolvable YET: "add my Necrons army, then a unit of warriors" creates the army in a
      // SIBLING action of this same turn that hasn't executed. Carry the NAME; execution
      // re-resolves (found live 2026-08-27: the unit vanished with "Add an army first").
      if (!fr.ok) {
        const fname = asStr(p.faction);
        if (fname) return { reply: say, action: { kind: "add_unit", factionId: "", factionLabel: fname, factionName: fname, name, points: asInt(p.points), modelCount: asInt(p.model_count) } };
        return none(fr.message);
      }
      return { reply: say, action: { kind: "add_unit", factionId: fr.faction.id, factionLabel: fr.faction.name, name, points: asInt(p.points), modelCount: asInt(p.model_count) } };
    }
    case "set_status": {
      const term = asStr(p.target);
      const status = asStatus(p.status);
      if (!term) return none(say || "Which unit should I update?");
      if (!status) return none(say || `What status — ${STATUSES.join(", ")}?`);
      const matches = await findUnitByName(term);
      if (matches.length > 1) {
        const list = matches.slice(0, 6).map((u) => `• ${u.name}`).join("\n");
        return none(`${matches.length} units match "${term}" — be more specific:\n${list}`);
      }
      // 0 matches does NOT fail here: "add a unit of warriors, still unpainted" creates the unit
      // in a SIBLING action of this turn that hasn't executed yet. Carry the NAME; execution
      // re-resolves (found live 2026-08-27, third action in the chain).
      if (matches.length === 0) return { reply: say, action: { kind: "set_status", id: "", unitName: term, label: `"${term}"`, status } };
      return { reply: say, action: { kind: "set_status", id: matches[0].id, label: `"${matches[0].name}"`, status } };
    }
    case "edit_unit": {
      const term = asStr(p.target);
      if (!term) return none(say || "Which unit should I update?");
      const matches = await findUnitByName(term);
      if (matches.length === 0) return none(await unitNotFound(term));
      if (matches.length > 1) {
        const list = matches.slice(0, 6).map((u) => `• ${u.name}`).join("\n");
        return none(`${matches.length} units match "${term}" — be more specific:\n${list}`);
      }
      const patch: UnitPatch = {};
      const bits: string[] = [];
      const name = asStr(p.name); if (name) { patch.name = name; bits.push(`name → ${name}`); }
      const points = asInt(p.points); if (points !== undefined) { patch.points = points; bits.push(`${points} pts`); }
      const models = asInt(p.model_count); if (models !== undefined) { patch.model_count = models; bits.push(`${models} models`); }
      const notes = asStr(p.notes); if (notes) { patch.notes = notes; bits.push("notes"); }
      const factionQuery = asStr(p.faction);
      if (factionQuery) {
        const fr = await resolveFaction(factionQuery);
        if (!fr.ok) return none(fr.message);
        patch.faction_id = fr.faction.id; bits.push(`army → ${fr.faction.name}`);
      }
      if (Object.keys(patch).length === 0) return none(say || "What should I change — name, points, models, notes, or army?");
      return { reply: say, action: { kind: "edit_unit", id: matches[0].id, patch, label: `"${matches[0].name}"${bits.length ? ` (${bits.join(" · ")})` : ""}` } };
    }
    case "delete_unit": {
      const term = asStr(p.target);
      if (!term) return none(say || "Which unit should I remove?");
      const matches = await findUnitByName(term);
      if (matches.length === 0) return none(await unitNotFound(term));
      if (matches.length > 1) {
        const list = matches.slice(0, 6).map((u) => `• ${u.name}`).join("\n");
        return none(`${matches.length} units match "${term}" — be more specific:\n${list}`);
      }
      return { reply: say, action: { kind: "delete_unit", id: matches[0].id, label: `"${matches[0].name}"` } };
    }

    // ── armies (factions) ──────────────────────────────────────────────────
    case "add_faction": {
      const name = asStr(p.name);
      if (!name) return none(say || "What should the army be called?");
      const input: NewFaction = { name };
      const gs = asStr(p.game_system); if (gs) input.game_system = gs;
      const notes = asStr(p.notes); if (notes) input.notes = notes;
      return { reply: say, action: { kind: "add_faction", input } };
    }
    case "edit_faction": {
      const term = asStr(p.target);
      if (!term) return none(say || "Which army should I update?");
      const fr = await resolveFaction(term);
      if (!fr.ok) return none(fr.message);
      const patch: FactionPatch = {};
      const bits: string[] = [];
      const name = asStr(p.name); if (name) { patch.name = name; bits.push(`name → ${name}`); }
      const gs = asStr(p.game_system); if (gs) { patch.game_system = gs; bits.push(`system → ${gs}`); }
      const notes = asStr(p.notes); if (notes) { patch.notes = notes; bits.push("notes"); }
      if (Object.keys(patch).length === 0) return none(say || "What should I change — name, game system, or notes?");
      return { reply: say, action: { kind: "edit_faction", id: fr.faction.id, patch, label: `"${fr.faction.name}"${bits.length ? ` (${bits.join(" · ")})` : ""}` } };
    }
    case "delete_faction": {
      const term = asStr(p.target);
      if (!term) return none(say || "Which army should I delete?");
      const fr = await resolveFaction(term);
      if (!fr.ok) return none(fr.message);
      return { reply: say, action: { kind: "delete_faction", id: fr.faction.id, label: `"${fr.faction.name}"` } };
    }

    // ── paint inventory ────────────────────────────────────────────────────
    case "add_paint": {
      const name = asStr(p.name);
      if (!name) return none(say || "What's the paint called?");
      const input: NewPaint = { name };
      const brand = asStr(p.brand); if (brand) input.brand = brand;
      const pt = asPaintType(p.paint_type); if (pt) input.paint_type = pt;
      const cf = asStr(p.color_family); if (cf) input.color_family = cf;
      const qty = asInt(p.qty); if (qty !== undefined) input.qty = qty;
      return { reply: say, action: { kind: "add_paint", input } };
    }
    case "edit_paint": {
      const term = asStr(p.target);
      if (!term) return none(say || "Which paint should I update?");
      const matches = await findPaintByName(term);
      if (matches.length === 0) return none(`I couldn't find a paint matching "${term}".`);
      if (matches.length > 1) return none(`${matches.length} paints match "${term}" — be more specific:\n${matches.slice(0, 6).map((pt) => `• ${pt.brand ? `${pt.brand} ` : ""}${pt.name}`).join("\n")}`);
      const patch: PaintPatch = {};
      const bits: string[] = [];
      const name = asStr(p.name); if (name) { patch.name = name; bits.push(`name → ${name}`); }
      const brand = asStr(p.brand); if (brand) { patch.brand = brand; bits.push(`brand → ${brand}`); }
      const pt = asPaintType(p.paint_type); if (pt) { patch.paint_type = pt; bits.push(`type → ${pt}`); }
      const cf = asStr(p.color_family); if (cf) { patch.color_family = cf; bits.push(`colour → ${cf}`); }
      const qty = asInt(p.qty); if (qty !== undefined) { patch.qty = qty; bits.push(`qty → ${qty}`); }
      if (Object.keys(patch).length === 0) return none(say || "What should I change — name, brand, type, colour, or quantity?");
      return { reply: say, action: { kind: "edit_paint", id: matches[0].id, patch, label: `"${matches[0].name}"${bits.length ? ` (${bits.join(" · ")})` : ""}` } };
    }
    case "delete_paint": {
      const term = asStr(p.target);
      if (!term) return none(say || "Which paint should I remove?");
      const matches = await findPaintByName(term);
      if (matches.length === 0) return none(`I couldn't find a paint matching "${term}".`);
      if (matches.length > 1) return none(`${matches.length} paints match "${term}" — be more specific:\n${matches.slice(0, 6).map((pt) => `• ${pt.brand ? `${pt.brand} ` : ""}${pt.name}`).join("\n")}`);
      return { reply: say, action: { kind: "delete_paint", id: matches[0].id, label: `"${matches[0].name}"` } };
    }

    // ── paint recipes ──────────────────────────────────────────────────────
    case "add_recipe": {
      const name = asStr(p.name);
      if (!name) return none(say || "What's the recipe called?");
      const input: NewRecipe = { name };
      const steps = (asStr(p.steps) ?? "").split("|").map((s) => s.trim()).filter(Boolean);
      if (steps.length) input.steps = steps;
      const notes = asStr(p.notes); if (notes) input.notes = notes;
      return { reply: say, action: { kind: "add_recipe", input } };
    }
    case "edit_recipe": {
      const term = asStr(p.target);
      if (!term) return none(say || "Which recipe should I update?");
      const matches = await findRecipeByName(term);
      if (matches.length === 0) return none(`I couldn't find a recipe matching "${term}".`);
      if (matches.length > 1) return none(`${matches.length} recipes match "${term}" — be more specific:\n${matches.slice(0, 6).map((rc) => `• ${rc.name}`).join("\n")}`);
      const patch: RecipePatch = {};
      const bits: string[] = [];
      const name = asStr(p.name); if (name) { patch.name = name; bits.push(`name → ${name}`); }
      const steps = (asStr(p.steps) ?? "").split("|").map((s) => s.trim()).filter(Boolean);
      if (steps.length) { patch.steps = steps; bits.push(`${steps.length} steps`); }
      const notes = asStr(p.notes); if (notes) { patch.notes = notes; bits.push("notes"); }
      if (Object.keys(patch).length === 0) return none(say || "What should I change — name, steps, or notes?");
      return { reply: say, action: { kind: "edit_recipe", id: matches[0].id, patch, label: `"${matches[0].name}"${bits.length ? ` (${bits.join(" · ")})` : ""}` } };
    }
    case "delete_recipe": {
      const term = asStr(p.target);
      if (!term) return none(say || "Which recipe should I delete?");
      const matches = await findRecipeByName(term);
      if (matches.length === 0) return none(`I couldn't find a recipe matching "${term}".`);
      if (matches.length > 1) return none(`${matches.length} recipes match "${term}" — be more specific:\n${matches.slice(0, 6).map((rc) => `• ${rc.name}`).join("\n")}`);
      return { reply: say, action: { kind: "delete_recipe", id: matches[0].id, label: `"${matches[0].name}"` } };
    }

    case "search_units": {
      const query = asStr(p.query);
      const status = asStatus(p.status);
      let factionId: string | undefined;
      let factionLabel: string | undefined;
      const factionQuery = asStr(p.faction);
      if (factionQuery) {
        const factions = await store.listFactions();
        const fq = factionQuery.toLowerCase();
        const match = factions.find((f) => f.name.toLowerCase().includes(fq));
        if (match) { factionId = match.id; factionLabel = match.name; }
      }
      return { reply: "", action: { kind: "search_units", query, status, factionId, factionLabel } };
    }
    case "recap": {
      const units = await store.listUnits();
      if (units.length === 0) return none("Add a few units first — there's nothing to recap yet.");
      try {
        const signals = await store.computeSignals(todayLocal());
        const rr = await recapCollection(signals);
        return { reply: "", action: { kind: "recap", text: rr.recap } };
      } catch (e) {
        if (e instanceof BrainError) return none(e.message);
        throw e;
      }
    }
    case "backup_save":
      return { reply: say, action: { kind: "backup_save" } };
    case "backup_status":
      return { reply: say, action: { kind: "backup_status" } };
    case "backup_restore": {
      const prov = asStr(p.provider);
      return { reply: say, action: { kind: "backup_restore", provider: prov === "github" || prov === "gdrive" ? prov : undefined } };
    }
    case "save_local_copy":
      return { reply: say, action: { kind: "save_local_copy" } };
    case "import_file":
      return { reply: say, action: { kind: "import_file" } };
    case "save_to_cloud": {
      const ck = asStr(p.content_kind) ?? "";
      const contentKind = /conversation|chat|messages|prompts/i.test(ck) ? "conversation" : "note";
      const dest = asStr(p.destination);
      const destination = dest ? (/drive|gdrive|google/i.test(dest) ? "gdrive" : /git\s*hub/i.test(dest) ? "github" : undefined) : undefined;
      return { reply: say, action: { kind: "save_to_cloud", contentKind, note: asStr(p.note) ?? asStr(p.content), destination } };
    }
    case "remember": {
      const instruction = asStr(p.instruction);
      if (!instruction) return none(say || "What should I remember?");
      return { reply: say, action: { kind: "remember", instruction } };
    }
    default:
      return none(say || HELP);
  }
}

export async function routeMessage(text: string): Promise<One> {
  const t = text.trim();
  if (!t) return { reply: "", action: { kind: "none", message: HELP } };

  // 1) ADD UNIT — "add unit <name>[ to <army>][, N points][, M models]".
  const addM = t.match(/^(?:add|create|log|new)\s+(?:a\s+|the\s+|new\s+)?unit\b[\s:.\-]*([\s\S]+)$/i);
  if (addM) {
    let body = addM[1].trim();
    if (!body) {
      return { reply: "", action: { kind: "none", message: 'Give me a name, e.g. "add unit Terminator Squad to Space Marines, 200 points, 5 models".' } };
    }
    let points: number | undefined;
    let modelCount: number | undefined;
    const ptsM = body.match(/(\d+)\s*points?\b/i);
    if (ptsM) { points = Number(ptsM[1]); body = body.replace(ptsM[0], ""); }
    const modM = body.match(/(\d+)\s*models?\b/i);
    if (modM) { modelCount = Number(modM[1]); body = body.replace(modM[0], ""); }
    body = body.replace(/,\s*,/g, ",").replace(/,\s*$/, "").replace(/\s{2,}/g, " ").trim();

    const toM = body.match(/^(.+?)\s+(?:to|in|for)\s+(.+)$/i);
    const name = (toM ? toM[1] : body).replace(/,\s*$/, "").trim();
    const factionQuery = toM ? toM[2].replace(/,\s*$/, "").trim() : null;
    if (!name) return { reply: "", action: { kind: "none", message: "I need a unit name." } };

    const factions = await store.listFactions();
    if (factions.length === 0) {
      return { reply: "", action: { kind: "none", message: "Add an army first (Collection tab → Add army) — then I can log units to it." } };
    }
    let faction: Faction | undefined;
    if (factionQuery) {
      const fq = factionQuery.toLowerCase();
      const matches = factions.filter((f) => f.name.toLowerCase().includes(fq));
      if (matches.length === 0) {
        const list = factions.map((f) => `• ${f.name}`).join("\n");
        return { reply: "", action: { kind: "none", message: `No army matching “${factionQuery}”. You have:\n${list}` } };
      }
      if (matches.length > 1) {
        const list = matches.map((f) => `• ${f.name}`).join("\n");
        return { reply: "", action: { kind: "none", message: `${matches.length} armies match “${factionQuery}” — be more specific:\n${list}` } };
      }
      faction = matches[0];
    } else if (factions.length === 1) {
      faction = factions[0];
    } else {
      const list = factions.map((f) => `• ${f.name}`).join("\n");
      return { reply: "", action: { kind: "none", message: `Which army? You have:\n${list}\nTry "add unit ${name} to <army name>".` } };
    }
    return { reply: "", action: { kind: "add_unit", factionId: faction.id, factionLabel: faction.name, name, points, modelCount } };
  }

  // 2) SET STATUS — the one supported "edit": "mark <unit> as <status>" / "set <unit> to <status>".
  const setM = t.match(new RegExp(`^(?:mark|set)\\s+(.+?)\\s+(?:as|to)\\s+(${STATUS_ALT}|work[\\s-]?in[\\s-]?progress)\\b.*$`, "i"));
  if (setM) {
    const term = setM[1].trim().replace(/[.?!]+$/, "");
    const status = (/work/i.test(setM[2]) ? "wip" : setM[2].toLowerCase()) as Status;
    if (!term) return { reply: "", action: { kind: "none", message: "Which unit should I update?" } };
    const matches = await findUnitByName(term);
    if (matches.length === 0) return { reply: "", action: { kind: "none", message: await unitNotFound(term) } };
    if (matches.length > 1) {
      const list = matches.slice(0, 6).map((u) => `• ${u.name}`).join("\n");
      return { reply: "", action: { kind: "none", message: `${matches.length} units match “${term}” — be more specific:\n${list}` } };
    }
    return { reply: "", action: { kind: "set_status", id: matches[0].id, label: `“${matches[0].name}”`, status } };
  }

  // 3) DELETE — resolve the target unit locally; the UI asks for confirmation before executing.
  const delM = t.match(/^(?:delete|remove|forget)\s+(?:the\s+)?(?:unit\s+)?(?:called\s+|named\s+|my\s+)?["']?(.+?)["']?\s*$/i);
  if (delM) {
    const term = delM[1].trim().replace(/[.?!]+$/, "");
    if (!term) return { reply: "", action: { kind: "none", message: "Which unit should I remove?" } };
    const matches = await findUnitByName(term);
    if (matches.length === 0) return { reply: "", action: { kind: "none", message: await unitNotFound(term) } };
    if (matches.length > 1) {
      const list = matches.slice(0, 6).map((u) => `• ${u.name}`).join("\n");
      return { reply: "", action: { kind: "none", message: `${matches.length} units match “${term}” — be more specific:\n${list}` } };
    }
    return { reply: "", action: { kind: "delete_unit", id: matches[0].id, label: `“${matches[0].name}”` } };
  }

  // 4) RECAP — the ONE billed call, reusing the EXISTING recapCollection()/`/recap` endpoint verbatim.
  if (RECAP_RE.test(t)) {
    const units = await store.listUnits();
    if (units.length === 0) {
      return { reply: "", action: { kind: "none", message: "Add a few units first — there's nothing to recap yet." } };
    }
    try {
      const signals = await store.computeSignals(todayLocal());
      const r = await recapCollection(signals);
      return { reply: "", action: { kind: "recap", text: r.recap } };
    } catch (e) {
      if (e instanceof BrainError) return { reply: "", action: { kind: "none", message: e.message } };
      throw e;
    }
  }

  // 5) SEARCH / FILTER — local list query (free, no bill).
  const listM = t.match(/^(?:show|list|find|search)\s+(?:me\s+)?(?:for\s+)?(.+)$/i);
  if (listM) {
    let q = listM[1].trim().replace(/[.?!]+$/, "");
    let status: Status | undefined;
    const statusHit = STATUSES.find((s) => new RegExp(`\\b${s}\\b`, "i").test(q));
    if (statusHit) { status = statusHit; q = q.replace(new RegExp(`\\b${statusHit}\\b`, "i"), "").trim(); }
    let factionId: string | undefined;
    let factionLabel: string | undefined;
    const inM = q.match(/^(?:units?\s+)?(?:in|from|for)\s+(.+)$/i);
    if (inM) {
      const factions = await store.listFactions();
      const fq = inM[1].trim().toLowerCase();
      const match = factions.find((f) => f.name.toLowerCase().includes(fq));
      if (match) { factionId = match.id; factionLabel = match.name; q = ""; }
    }
    q = q.replace(/\bunits?\b/i, "").trim();
    return { reply: "", action: { kind: "search_units", query: q || undefined, status, factionId, factionLabel } };
  }

  // 6) Unclear → a short clarifying nudge.
  return { reply: "", action: { kind: "none", message: HELP } };
}

// Resolve a library title to exactly one item, or explain why not (same 0/1/many
// contract the app uses for its own entries).
function resolveLibraryItem(term: string | undefined): { item?: import("../library").LibraryItem; problem?: string } {
  if (!term) return { problem: "Which saved item do you mean? Give me its title." };
  const found = libraryFind(term);
  if (found.length === 0) return { problem: `I couldn't find "${term}" in your library.` };
  if (found.length > 1) return { problem: `I found ${found.length} matching "${term}" — be more specific: ${found.map((f) => `"${f.title}"`).join(", ")}` };
  return { item: found[0] };
}
